import { Box, HStack, Text } from '@chakra-ui/react'
import { useEffect, useRef } from 'react'
import { Transforms } from 'slate'
import { ReactEditor, useSlate } from 'slate-react'

import Avatar from '@/components/Worlds/UI/Avatar/Avatar'

import { MENTION_SUGGESTION_LIST_CLASS_NAME } from '../../constants'
import type { UserMention } from '../../types'
import { insertMention } from '../inlines'
import { useMentionsContext } from './context'
import { MENTIONS_QUERY_THRESHOLD } from './MentionsContextProvider'

const MAX_MENU_HEIGHT_PX = 230

export default function MentionSuggestionList(): JSX.Element | null {
  const { filter, menuIndex, setMenuIndex, userList, rangeToReplace, closeMentionsMenu } =
    useMentionsContext()

  const editor = useSlate()
  const ref = useRef<HTMLDivElement>(null)

  // scrolls selected menu item into view
  useEffect(() => {
    if (ref.current != null) {
      const selected = ref.current.querySelector(`[data-index="${menuIndex}"]`)
      if (selected != null) {
        selected.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      }
    }
  }, [menuIndex])

  if (rangeToReplace == null) {
    return null
  }

  const handleOptionSelected = (user: UserMention) => {
    Transforms.select(editor, rangeToReplace)
    insertMention(editor, user)
    closeMentionsMenu()
    ReactEditor.focus(editor)
  }

  return (
    <Box
      ref={ref}
      overflow="auto"
      p={0}
      position="absolute"
      width="100%"
      bottom="calc(100% + 12px)"
      maxH={`${MAX_MENU_HEIGHT_PX}px`}
      bg="bg"
      border="2px solid"
      borderColor="border.emphasized"
      borderRadius="8px"
      className={MENTION_SUGGESTION_LIST_CLASS_NAME}
    >
      {userList.map((user, index) => (
        <HStack
          key={user.id}
          data-index={index}
          onClick={() => handleOptionSelected(user)}
          onMouseEnter={() => setMenuIndex(index)}
          onMouseLeave={() => setMenuIndex(-1)}
          cursor="pointer"
          background={index === menuIndex ? 'primary.solid' : 'bg'}
          spacing="8px"
          p="8px"
          align="center"
        >
          <Avatar user={{ ...user, avatarUrl: user.avatarUrl ?? '' }} size="xs" hideFrame />
          <Text color={index === menuIndex ? 'primary.contrast' : 'fg'}>{user.displayName}</Text>
          {user.username == null ? null : (
            <Text color={index === menuIndex ? 'primary.contrast' : 'fg.muted'}>
              @{user.username}
            </Text>
          )}
        </HStack>
      ))}

      {userList.length === 0 ? (
        <Box p="8px">
          {filter.length >= MENTIONS_QUERY_THRESHOLD ? (
            <Text color="fg.subtle">No results.</Text>
          ) : (
            <Text color="fg.subtle">Keep typing to see more results.</Text>
          )}
        </Box>
      ) : null}
    </Box>
  )
}
